import * as React from "react";
import { Check, CheckCheck, Loader2, Search, X } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from "~/components/ui/command";
import { ImageLoader } from "~/components/ImageLoader";
import { useDebouncedState } from "~/utils/useDebouncedInput";
import {
    ApiQuery,
    Filter,
    FilterOperators,
    QueryResponse,
    SortDirections,
} from "~/modules/filters/types";
import { api } from "~/utils/api";
import { deobfuscate } from "~/utils/obfuscate";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { RWA_COIN_ICON, TOKENIZATION_TYPE_IDS } from "~/consts";
import { AssetClassPage } from "~/modules/asset_classes/types";
import { ASSET_CLASS_PAGES_TO_FILTER } from "~/modules/asset_classes/consts";
import {
    ASSET_CLASS_PAGE_ORDER,
    ASSET_CLASS_PAGE_TO_LABEL,
    ASSET_CLASS_PAGES_TO_ICON,
} from "~/modules/asset_classes/metadata";
import { Icon } from "~/components/Icon";
import { IconTypes } from "~/components/Icon/types";

// // // //

// Upper bound on how many assets a single preset (asset class) query fetches, so
// "Select all" can grab the entire class in one request.
export const PRESET_MAX_ASSETS = 200;

// Max rows rendered in the picker list — keeps the popover snappy even when a
// preset matches thousands of assets ("Select all" still grabs the full set).
const PICKER_VISIBLE_LIMIT = 100;

// // // //

export interface Preset {
    key: AssetClassPage;
    label: string;
    filter: Filter;
    icon: IconTypes;
}

export const PRESETS: Preset[] = ASSET_CLASS_PAGE_ORDER.map((page) => ({
    key: page,
    label: ASSET_CLASS_PAGE_TO_LABEL[page],
    filter: ASSET_CLASS_PAGES_TO_FILTER[page],
    icon: ASSET_CLASS_PAGES_TO_ICON[page],
}));

// // // //

export const selectAssetQueryResponse = (data: unknown) => {
    return deobfuscate(data as string) as QueryResponse<Asset>;
};

export function getSearchOptionLabel(asset: Asset) {
    if (asset.ticker) {
        return `${asset.name} (${asset.ticker})`;
    }

    return asset.name;
}

function escapeSearchValue(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildSearchFilter(props: {
    search: string;
    presetFilter?: Filter;
}): Filter {
    const filters: Filter[] = [
        {
            field: AssetColumns.tokenCount,
            operator: FilterOperators.GreaterThan,
            value: 0,
        },
        {
            field: AssetColumns.tokenizationTypeIDs,
            operator: FilterOperators.NotIncludes,
            value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
        },
        {
            operator: FilterOperators.Or,
            filters: [
                {
                    field: AssetColumns.adjustedDailyMintsDollar,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
                {
                    field: AssetColumns.adjustedDailyBurnsDollar,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
            ],
        },
    ];

    // Restrict to a single asset class when a preset chip is active.
    if (props.presetFilter) {
        filters.push(props.presetFilter);
    }

    if (props.search.trim() !== "") {
        const escapedSearch = escapeSearchValue(props.search.trim());

        filters.push({
            operator: FilterOperators.Or,
            filters: [
                {
                    field: "name",
                    operator: FilterOperators.Like,
                    value: escapedSearch,
                },
                {
                    field: "ticker",
                    operator: FilterOperators.Like,
                    value: escapedSearch,
                },
                {
                    field: "slug",
                    operator: FilterOperators.Like,
                    value: escapedSearch,
                },
            ],
        });
    }

    return {
        operator: FilterOperators.And,
        filters,
    };
}

// // // //

/**
 * AssetPicker
 * Combined search + asset-class presets in a single popover. Typing searches all
 * assets; the chips below the input scope the list to one asset class. Clicking a
 * row toggles it in the selection, and "Select all" adds every asset matching the
 * active preset.
 */
export function AssetPicker(props: {
    selectedAssetIDs: number[];
    atLimit: boolean;
    onToggle: (asset: Asset) => void;
    onSelectMany: (assets: Asset[]) => void;
    onDeselectMany: (assets: Asset[]) => void;
}) {
    const {
        selectedAssetIDs,
        atLimit,
        onToggle,
        onSelectMany,
        onDeselectMany,
    } = props;

    const [open, setOpen] = React.useState(false);
    const [activePreset, setActivePreset] =
        React.useState<AssetClassPage | null>(null);

    const [debouncedSearch, setDebouncedSearch] = React.useState("");
    const [inputValue, setInputValue] = useDebouncedState<string>({
        value: debouncedSearch,
        debounceTimeout: 250,
        onChange: setDebouncedSearch,
    });

    const activePresetFilter = React.useMemo(
        () =>
            activePreset
                ? ASSET_CLASS_PAGES_TO_FILTER[activePreset]
                : undefined,
        [activePreset],
    );

    // Build the assets API query from search + preset state. Memoized so useQuery
    // only sees a new `query` reference when those inputs change, not on every render.
    const searchQuery: ApiQuery = React.useMemo(
        () => ({
            name: "asset-picker-search",
            fields: [
                "id",
                "name",
                "ticker",
                "slug", // ENHANCEMENT - add slug to data catalog?
                "icon_url",
            ],
            sort: {
                field: AssetColumns.bridgedTokenValueDollar,
                direction: SortDirections.desc,
            },
            pagination: {
                page: 1,
                // Fetch the full class when a preset is active so "Select all"
                // can grab everything; otherwise just a page of search results.
                perPage: activePreset ? PRESET_MAX_ASSETS : 50,
            },
            filter: buildSearchFilter({
                search: debouncedSearch,
                presetFilter: activePresetFilter,
            }),
        }),
        [debouncedSearch, activePreset, activePresetFilter],
    );

    // keepPreviousData retains the current results while the next query (e.g. a
    // preset switch) loads, so the list stays put under the loading overlay.
    const searchResultsReq = api.assets.query.useQuery(
        { query: searchQuery },
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            trpc: { context: { skipBatch: true } },
            select: selectAssetQueryResponse,
        },
    );

    const resultAssets = React.useMemo(
        () => (searchResultsReq.data?.results || []) as Asset[],
        [searchResultsReq.data?.results],
    );

    const selectedIDSet = React.useMemo(
        () => new Set(selectedAssetIDs),
        [selectedAssetIDs],
    );

    const visibleAssets = resultAssets.slice(0, PICKER_VISIBLE_LIMIT);

    // Whether every asset in the current result set is already selected.
    const allSelected =
        resultAssets.length > 0 &&
        resultAssets.every((asset) => selectedIDSet.has(asset.id));

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md border bg-card px-3 py-3 text-base text-muted-foreground transition-colors hover:bg-muted"
                >
                    <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
                    Search or browse assets…
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-(--radix-popover-trigger-width) p-0"
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        value={inputValue}
                        onValueChange={(value) => {
                            setInputValue(value);
                            // Searching is global — drop any active class filter
                            // so results aren't scoped to that subset.
                            if (value.trim() !== "") {
                                setActivePreset(null);
                            }
                        }}
                        // Intentionally not disabled while fetching: a disabled
                        // input loses focus, kicking the cursor out mid-type each
                        // time results load. The list overlay signals loading.
                        placeholder="Search tokenized assets…"
                    />

                    <div className="flex flex-wrap items-center gap-1 border-b p-2">
                        {PRESETS.map((preset) => {
                            const isActive = activePreset === preset.key;
                            return (
                                <button
                                    key={preset.key}
                                    type="button"
                                    onClick={() => {
                                        setActivePreset(
                                            isActive ? null : preset.key,
                                        );
                                        // Browsing a class is its own scope —
                                        // clear any active search query.
                                        setInputValue("");
                                        setDebouncedSearch("");
                                    }}
                                    disabled={searchResultsReq.isFetching}
                                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors disabled:pointer-events-none disabled:opacity-50 ${
                                        isActive
                                            ? "border-primary bg-primary text-primary-foreground"
                                            : "bg-card text-foreground hover:bg-muted"
                                    }`}
                                >
                                    <Icon
                                        icon={preset.icon}
                                        className="h-3 w-3 shrink-0"
                                    />
                                    {preset.label}
                                </button>
                            );
                        })}
                    </div>

                    {resultAssets.length > 0 && (
                        <div className="flex items-center gap-3 border-b px-3 py-1.5">
                            <span className="text-xs text-muted-foreground">
                                {resultAssets.length} assets
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    allSelected
                                        ? onDeselectMany(resultAssets)
                                        : onSelectMany(resultAssets)
                                }
                                disabled={
                                    searchResultsReq.isFetching ||
                                    (!allSelected && atLimit)
                                }
                                className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                            >
                                {allSelected ? (
                                    <X className="h-3.5 w-3.5 shrink-0" />
                                ) : (
                                    <CheckCheck className="h-3.5 w-3.5 shrink-0" />
                                )}
                                {allSelected ? "Deselect all" : "Select all"}
                            </button>
                        </div>
                    )}

                    <div className="relative">
                        {searchResultsReq.isFetching && (
                            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-popover/30 backdrop-blur-[1px]">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                        )}

                        <CommandList
                            className={`transition-opacity duration-200 ${
                                searchResultsReq.isFetching
                                    ? "opacity-40"
                                    : "opacity-100"
                            }`}
                        >
                            {searchResultsReq.data &&
                                resultAssets.length === 0 && (
                                    <CommandEmpty>
                                        No assets found.
                                    </CommandEmpty>
                                )}

                            {visibleAssets.map((asset) => {
                                const isSelected = selectedIDSet.has(asset.id);
                                return (
                                    <CommandItem
                                        key={asset.id}
                                        value={String(asset.id)}
                                        disabled={!isSelected && atLimit}
                                        onSelect={() => onToggle(asset)}
                                        className="flex items-center gap-2"
                                    >
                                        <ImageLoader
                                            draggable={false}
                                            className="w-4 shrink-0 rounded-xs"
                                            src={
                                                asset.icon_url || RWA_COIN_ICON
                                            }
                                            alt={`${asset.name} Icon`}
                                            fallbackSrc={RWA_COIN_ICON}
                                        />
                                        <span className="flex-1 truncate">
                                            {getSearchOptionLabel(asset)}
                                        </span>
                                        {isSelected && (
                                            <Check className="h-4 w-4 shrink-0 text-primary" />
                                        )}
                                    </CommandItem>
                                );
                            })}

                            {resultAssets.length > PICKER_VISIBLE_LIMIT && (
                                <p className="px-3 py-2 text-center text-xs text-muted-foreground">
                                    Showing first {PICKER_VISIBLE_LIMIT} of{" "}
                                    {resultAssets.length}
                                    {activePreset ? " — use “Select all”" : ""}
                                </p>
                            )}
                        </CommandList>
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
